const rydeMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
const rydeCompactQuery = window.matchMedia('(max-width: 767px)');
const rydeFind = (scope, selector) => [
  ...(scope.matches?.(selector) ? [scope] : []),
  ...scope.querySelectorAll(selector),
];

const applyRydeIngredientDefaults = (section) => {
  if (section.dataset.detailsInteracted === 'true') return;
  section.querySelectorAll('[data-formula-panel]').forEach((panel) => {
    panel.querySelectorAll('.ryde-cro-ingredient').forEach((detail, index) => {
      detail.open = index === 0;
    });
  });
};

const bindScrollControls = (root, trackSelector, previousSelector, nextSelector, distance, navSelector) => {
  const track = root.querySelector(trackSelector);
  const previous = root.querySelector(previousSelector);
  const next = root.querySelector(nextSelector);
  const nav = navSelector ? root.querySelector(navSelector) : null;
  if (!track || !previous || !next) return;

  const update = () => {
    const max = track.scrollWidth - track.clientWidth;
    if (nav) nav.hidden = max <= 2;
    previous.disabled = track.scrollLeft <= 2;
    next.disabled = track.scrollLeft >= max - 2;
  };
  const move = (direction) => {
    track.scrollBy({ left: direction * distance(track), behavior: rydeMotionQuery.matches ? 'auto' : 'smooth' });
  };

  previous.addEventListener('click', () => move(-1));
  next.addEventListener('click', () => move(1));
  track.addEventListener('scroll', update, { passive: true });
  update();
  requestAnimationFrame(update);
  window.addEventListener('load', update, { once: true });
  if ('ResizeObserver' in window) new ResizeObserver(update).observe(track);
};

const initializeRydeProducts = (scope) => {
  for (const root of rydeFind(scope, '[data-ryde-products]:not([data-initialized])')) {
    root.dataset.initialized = 'true';
    bindScrollControls(root, '[data-product-track]', '[data-product-prev]', '[data-product-next]', (track) => {
      const card = track.querySelector('.ryde-cro-product');
      return card ? card.getBoundingClientRect().width + 7 : track.clientWidth;
    });
  }
};

const initializeRydeIngredients = (scope) => {
  for (const section of rydeFind(scope, '[data-ingredients]:not([data-initialized])')) {
    section.dataset.initialized = 'true';
    const tabs = [...section.querySelectorAll('[data-formula-tab]')];
    const panels = [...section.querySelectorAll('[data-formula-panel]')];
    const heading = section.querySelector('[data-formula-heading]');

    // Each formula starts in the Figma state: first ingredient open, the rest closed.
    // Native grouped details keep the rows independently closable and prevent stacked panels.
    applyRydeIngredientDefaults(section);

    const select = (index, moveFocus = false) => {
      tabs.forEach((tab, tabIndex) => {
        const selected = tabIndex === index;
        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;
        panels[tabIndex].hidden = !selected;
      });
      section.style.setProperty('--formula-color', tabs[index].dataset.formulaColor);
      section.style.setProperty('--formula-tab-ink', tabs[index].dataset.formulaTabInk || '#fff');
      if (heading) heading.textContent = tabs[index].dataset.formulaName;
      if (moveFocus) tabs[index].focus();
    };

    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => select(index));
      tab.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        event.stopPropagation();
        const target = event.key === 'Home'
          ? 0
          : event.key === 'End'
            ? tabs.length - 1
            : (index + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
        select(target, true);
      });
    });
    select(Math.max(0, tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true')));
  }
};

const initializeRydeComparison = (scope) => {
  for (const root of rydeFind(scope, '[data-ryde-compare]:not([data-initialized])')) {
    root.dataset.initialized = 'true';
    bindScrollControls(root, '[data-compare-scroll]', '[data-compare-prev]', '[data-compare-next]', () => 103, '.ryde-cro-compare__nav');
  }
};

const initializeRydePlans = (scope) => {
  for (const root of rydeFind(scope, '[data-ryde-plans]:not([data-initialized])')) {
    root.dataset.initialized = 'true';
    const tabs = [...root.querySelectorAll('[data-plan-tab]')];
    const panels = [...root.querySelectorAll('[data-plan-panel]')];
    let selectedIndex = 0;

    const render = (moveFocus = false) => {
      tabs.forEach((tab, index) => {
        const selected = index === selectedIndex;
        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;
        panels[index].hidden = rydeCompactQuery.matches && !selected;
      });
      if (moveFocus) tabs[selectedIndex].focus();
    };

    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => {
        selectedIndex = index;
        render();
      });
      tab.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        event.stopPropagation();
        selectedIndex = event.key === 'Home'
          ? 0
          : event.key === 'End'
            ? tabs.length - 1
            : (index + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
        render(true);
      });
    });
    rydeCompactQuery.addEventListener('change', () => render());
    render();
  }
};

const initializeRydeShopMenuTriggers = (scope) => {
  for (const trigger of rydeFind(scope, '[data-ryde-shop-menu-trigger]:not([data-initialized])')) {
    trigger.dataset.initialized = 'true';
    trigger.addEventListener('click', () => {
      const shopLink = [...document.querySelectorAll('.header-menu a')].find((link) => (
        link.textContent.replace(/\s+/g, ' ').trim().toLowerCase() === 'shop'
        && link.hasAttribute('aria-controls')
      ));
      if (!shopLink) return;
      shopLink.dispatchEvent(new MouseEvent('mouseenter'));
      const menu = document.getElementById(shopLink.getAttribute('aria-controls'));
      window.setTimeout(() => menu?.querySelector('.mega-menu__card, a[href], button')?.focus(), 50);
    });
  }
};

const initializeRydeHome = (scope = document) => {
  initializeRydeProducts(scope);
  initializeRydeIngredients(scope);
  initializeRydeComparison(scope);
  initializeRydePlans(scope);
  initializeRydeShopMenuTriggers(scope);
};

document.addEventListener('click', (event) => {
  if (!event.isTrusted) return;
  const summary = event.target.closest?.('.ryde-cro-ingredient summary');
  summary?.closest('[data-ingredients]')?.setAttribute('data-details-interacted', 'true');
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initializeRydeHome(), { once: true });
} else {
  initializeRydeHome();
}
document.addEventListener('shopify:section:load', (event) => initializeRydeHome(event.target));
document.addEventListener('shopify:section:unload', (event) => {
  rydeFind(event.target, '[data-ryde-stories]')[0]?.rydeYotpoCleanup?.();
});
